import { LayoutDashboard, User, Users, DollarSign, CreditCard, Share2, LogOut, Menu, X, Award, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import type { PortalUser } from './AffiliatePortalApp';
import ThemeToggle from './ThemeToggle';

const MAIN_SITE = 'https://oasisautokonnect.com';

interface PortalLayoutProps {
  user: PortalUser;
  currentPath: string;
  navigate: (path: string) => void;
  onLogout: () => void;
  children: React.ReactNode;
}

const nav = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/referrals', label: 'My Referrals', icon: Users },
  { path: '/earnings', label: 'Earnings', icon: DollarSign },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/tree', label: 'My Network', icon: Share2 },
  { path: '/referral-code', label: 'Referral Link', icon: Award },
  { path: '/profile', label: 'Profile', icon: User },
];

export default function PortalLayout({ user, currentPath, navigate, onLogout, children }: PortalLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const impersonating = !!user.impersonated_by;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-inter">
      {impersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 text-sm flex items-center justify-between sticky top-0 z-50">
          <div>
            <strong>⚠ Admin impersonation:</strong> viewing the portal as <strong>{user.display_name}</strong> ({user.email}). This affiliate cannot see this banner; you are seeing it because your session was issued by admin. Every action is logged.
          </div>
          <button
            onClick={onLogout}
            className="ml-4 px-3 py-1 bg-amber-700 hover:bg-amber-800 rounded text-xs font-medium whitespace-nowrap"
          >
            Exit impersonation
          </button>
        </div>
      )}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 dark:bg-slate-950 border-r border-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <img
              src="https://oasisautokonnect.com/assets/images/Full-Name-White.png"
              alt="Oasis Auto Konnect"
              className="h-7 w-auto"
              loading="eager"
            />
            <span className="text-slate-300 text-xs font-medium uppercase tracking-wide">Affiliates</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="p-1 text-slate-400 hover:text-white lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {nav.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
                  isActive ? 'bg-cyan-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
          <a
            href={MAIN_SITE}
            className="w-full flex items-center px-4 py-3 mb-1 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <ExternalLink className="w-5 h-5 mr-3" />
            <span className="font-medium">Main Site</span>
          </a>
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-slate-800">
          <div className="text-sm text-white truncate">{user.display_name}</div>
          <div className="text-xs text-slate-400 truncate">{user.email}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            <span className="font-mono text-cyan-400">{user.referral_code}</span> · {user.status}
          </div>
          <button onClick={onLogout} className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-300 hover:bg-slate-800 rounded text-sm">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            <button onClick={() => setSidebarOpen(true)} className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white lg:hidden">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="hidden lg:block text-lg font-semibold text-slate-900 dark:text-white">
              {nav.find((n) => n.path === currentPath)?.label || 'OAK Affiliate Portal'}
            </h1>
            <div className="flex items-center gap-3">
              <div className="text-sm text-slate-600 dark:text-slate-300 hidden md:block">{user.display_name}</div>
              <ThemeToggle className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800" />
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          {user.status === 'onboarding' && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-sm text-yellow-900 dark:text-yellow-200">
              <strong>Onboarding:</strong> Complete your <button onClick={() => navigate('/profile')} className="underline font-medium">profile (legal name + TIN)</button> to be enrolled for commissions.
            </div>
          )}
          {user.status === 'suspended' && (
            <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded text-sm text-orange-900 dark:text-orange-200">
              Your account is currently <strong>suspended</strong>. Commissions continue to accrue but payments are paused. Contact support if you have questions.
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
